import {
  type CSSProperties,
  type FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  createPortal,
} from "react-dom";
import {
  useTranslation,
} from "react-i18next";
import {
  Spin,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import jsPDF from "jspdf";
import {
  Check,
  ChevronDown,
  X,
  TrendingUp,
  ShoppingCart,
  Package,
  SearchX,
  Download,
} from "lucide-react";
import {
  type SalesCustomer,
  type SalesProduct,
} from "./SalesProductAnalyticsTabsDashboard.data";
import {
  type ShopifySalesProductCustomersSort,
} from "src/services/ShopifySalesProductAPI";
import {
  getApiLangFromI18n,
} from "src/utils/apiLang";
import {
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";
import {
  useLanguage,
} from "src/hooks/useLanguage";
import {
  useShopifySalesProductOrderDetail,
} from "../hooks/useShopifySalesProductAnalytics";
import {
  type SalesProductOrderDetailView,
  type SalesProductOverviewTopProduct,
} from "../utils/salesProductApiNormalize";
import {
  channelDistributionBarColor,
  channelSharePercent,
  formatChannelPercent,
  type ChannelBarPoint,
} from "../utils/salesChannelDistributionChartModel";
import {
  DashboardKpiCard,
  DefinitionTooltip,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableTable,
  sortRowsByKeyAccessor,
  dashboardDataTable as dt,
  type DashboardTableColumnSort,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  CHART_COLOR_PALETTE,
  CHART_PRIMARY_COLOR,
  CHART_SECONDARY_COLOR,
  CHART_TERTIARY_COLOR,
  CHART_WARNING_COLOR,
} from "src/components/charts/chartPalette";

export type TrendGranularity = "daily" | "monthly";
export const TREND_PAGE_SIZE = 30;

export const INVENTORY_STOCK_BAR_COLORS = [
  CHART_PRIMARY_COLOR,
  CHART_COLOR_PALETTE[5],
  CHART_COLOR_PALETTE[6],
  CHART_COLOR_PALETTE[4],
  CHART_SECONDARY_COLOR,
  CHART_COLOR_PALETTE[5],
  CHART_COLOR_PALETTE[6],
  CHART_COLOR_PALETTE[4],
] as const;

const SALES_PRODUCT_SEGMENTATION_COLORS = [
  CHART_PRIMARY_COLOR,
  CHART_SECONDARY_COLOR,
] as const;

export type SalesChannelMetric = "sales" | "orders";

const SALES_STATUS_ORDER = ["fulfilled", "unfulfilled"] as const;
const SALES_STATUS_LABELS: Record<string, string> = {
  fulfilled: "Fulfilled",
  unfulfilled: "Unfulfilled",
};
const SALES_STATUS_DESCRIPTIONS: Record<string, string> = {
  fulfilled: "Orders that have been fulfilled.",
  unfulfilled: "Orders that have been placed but have not yet been fulfilled.",
};
const SALES_STATUS_COLORS: Record<string, string> = {
  fulfilled: "#17b26a",
  unfulfilled: "#f59e0b",
};

const TOP_CHANNEL_STATUS_DUMMY_ROWS = [
  { status: "fulfilled", label: "Fulfilled", count: 1820 },
  { status: "unfulfilled", label: "Unfulfilled", count: 1470 },
];

export const SALES_PRODUCT_FINANCIAL_COLORS = {
  revenue: CHART_PRIMARY_COLOR,
  expenses: CHART_TERTIARY_COLOR,
  profit: CHART_SECONDARY_COLOR,
  taxes: CHART_WARNING_COLOR,
  refunds: CHART_COLOR_PALETTE[4],
  cogs: CHART_COLOR_PALETTE[5],
  other: CHART_COLOR_PALETTE[6],
} as const;

export const getFinancialBreakdownColor = (label: string) => {
  const normalized = label.trim().toLowerCase();
  if (normalized.includes("revenue")) {
    return SALES_PRODUCT_FINANCIAL_COLORS.revenue;
  }
  if (normalized.includes("profit")) {
    return SALES_PRODUCT_FINANCIAL_COLORS.profit;
  }
  if (normalized.includes("tax")) {
    return SALES_PRODUCT_FINANCIAL_COLORS.taxes;
  }
  if (normalized.includes("refund")) {
    return SALES_PRODUCT_FINANCIAL_COLORS.refunds;
  }
  if (normalized.includes("cogs") || normalized.includes("cost of goods")) {
    return SALES_PRODUCT_FINANCIAL_COLORS.cogs;
  }
  if (normalized.includes("expense") || normalized.includes("cost")) {
    return SALES_PRODUCT_FINANCIAL_COLORS.expenses;
  }
  return SALES_PRODUCT_FINANCIAL_COLORS.other;
};

export const PAGE_SIZE = 10;
export const NOT_AVAILABLE = "Not Available";
const DEFAULT_LOW_STOCK_THRESHOLD = 10;

const MISSING_DISPLAY_TEXTS = new Set(["-", "—", "â€”", "Ã¢â‚¬â€"]);

const normalizeDisplayText = (value: unknown) => String(value ?? "").trim();

const isMissingDisplayText = (value: unknown) => {
  const text = normalizeDisplayText(value);
  return text === "" || MISSING_DISPLAY_TEXTS.has(text);
};

export const formatCurrency = (value: number, currencyLabel = "PKR") =>
  `${currencyLabel} ${Math.round(value).toLocaleString()}`;

const wrapInLtrIsolate = wrapBidiIsolate;

/**
 * Chart axis ticks, bar labels and tooltips are computed client-side, so unlike table cells
 * they have no API `*FormattedValue` sibling to fall back on — convert their digits here.
 * Values are isolated LTR because Arabic-Indic digits mixed with `-` / `,` / `%` would
 * otherwise be reordered by the bidi algorithm and read as the wrong date or amount.
 */
export const localizeChartDigits = (value: string, isRTL: boolean) =>
  isRTL ? wrapInLtrIsolate(toArabicIndicDigitsInString(value)) : value;

/**
 * Interpolated numbers inside translated copy (e.g. "Page {{current}} of {{total}}").
 * Unlike `localizeChartDigits`, these must not be wrapped in an LTR isolate — that
 * would break Arabic word order around the number. Convert digits only.
 */
export const localizeInlineDigits = (value: string | number, isRTL: boolean) =>
  isRTL ? toArabicIndicDigitsInString(String(value)) : String(value);

/**
 * Recharts axis/label mirroring for Arabic. Shared with the other dashboards — see
 * `src/components/charts/chartRtl.ts` for why the surface stays LTR while layout is flipped
 * through props.
 */
export { mirrorChartMargin, rtlChartProps } from "src/components/charts/chartRtl";

export const formatTableText = (value: unknown) =>
  isMissingDisplayText(value) ? NOT_AVAILABLE : normalizeDisplayText(value);

const formatOrderPanelText = (value: unknown) =>
  isMissingDisplayText(value) ? NOT_AVAILABLE : normalizeDisplayText(value);


export const formatOrderDate = (dateStr: string) => {
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : (dateStr || NOT_AVAILABLE);
};

export const formatInventoryTableValue = formatOrderPanelText;

export const formatOrderTableCurrency = (value: number) =>
  Number.isFinite(value) ? formatCurrency(value) : NOT_AVAILABLE;

const statusClassName = (status: string) =>
  status.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_") || "unknown";

const formatOrderDateTime = (value: string) => {
  if (!value) {
    return "—";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY hh:mm A") : value;
};

const formatOrderPanelDateTime = (value: string) => {
  if (!value) {
    return "â€”";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY, h:mm A") : value;
};

const formatOrderStatusLabel = (value: string) => {
  const label = String(value || "").trim();
  return label
    ? label.replace(/[_-]+/g, " ").replace(/\b\w/g, char => char.toUpperCase())
    : "â€”";
};

export const formatCurrencyTooltipValue = (value: number, currencyLabel = "PKR") =>
  formatCurrency(value, currencyLabel);
export const formatNumberTooltipValue = (value: number) => String(value);

const formatOrderPanelDateTimeDisplay = (value: string, isAr = false) => {
  const text = formatOrderPanelText(value);
  if (text === NOT_AVAILABLE) {
    return NOT_AVAILABLE;
  }
  const parsed = isAr ? dayjs(text).locale("ar") : dayjs(text);
  return parsed.isValid() ? parsed.format("MMM D, YYYY, h:mm A") : text;
};

const resolveOrderDateTimeDisplay = (
  order: { orderDateTime: string; orderDateTimeFormattedValue?: string },
  isAr: boolean,
) => {
  if (order.orderDateTimeFormattedValue) {
    return isAr ? wrapBidiIsolate(order.orderDateTimeFormattedValue) : order.orderDateTimeFormattedValue;
  }
  return formatOrderPanelDateTimeDisplay(order.orderDateTime, isAr);
};

const formatOrderStatusLabelDisplay = (value: string) => {
  const label = formatOrderPanelText(value);
  if (label === NOT_AVAILABLE) {
    return label;
  }
  return label.replace(/[_-]+/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

const getOrderStatusBadgeModifier = (value: string) => {
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-");
  if (normalized.includes("pending")) {
    return "pending";
  }
  if (normalized === "paid" || normalized === "partially-paid") {
    return "paid";
  }
  return "default";
};

export const TOTAL_SALES_METRIC_LABEL = "Total Sales";
const TOTAL_SALES_METRIC_FORMULA =
  "Total Sales = Gross Sales - Discounts - Returns + Additional Fees + Duties + Shipping Charges + Taxes";
const TOTAL_SALES_BY_CHANNEL_FORMULA =
  "Channel Distribution = Total Sales grouped by Sales Channel";
export const SALES_TREND_FORMULA =
  "Sales Trend = Daily Total Sales across selected date range";

const tableTotalPages = (totalRows: number) =>
  Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

export const getStockAxisMax = (rows: Array<{ stock: number }>) => {
  const maxStock = Math.max(0, ...rows.map(row => row.stock));
  return maxStock ? Math.ceil((maxStock * 1.18) / 2500) * 2500 : 100;
};

export const getStockReferenceTicks = (axisMax: number) =>
  Array.from({ length: Math.max(0, Math.floor(axisMax / 2500)) }, (_, index) => (index + 1) * 2500);

export const getTenantLowStockStorageKey = (tenantId?: number) =>
  `sales_product_low_stock_threshold_${tenantId ?? "default"}`;

export const readStoredLowStockThreshold = (tenantId?: number) => {
  if (typeof localStorage === "undefined") {
    return DEFAULT_LOW_STOCK_THRESHOLD;
  }
  const n = Number(localStorage.getItem(getTenantLowStockStorageKey(tenantId)));
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : DEFAULT_LOW_STOCK_THRESHOLD;
};

export const resolveInventoryStatusByThreshold = (
  stock: number,
  threshold: number,
): SalesProduct["status"] => {
  if (stock <= 0) {
    return "out_of_stock";
  }
  return stock <= threshold ? "low_stock" : "in_stock";
};

export type CustomerTableSortKey = "totalOrders" | "totalSpent" | "recentOrderDate" | "signUpDate";

export const isDefaultCustomerTableSort = (
  sort: DashboardTableColumnSort<CustomerTableSortKey>,
) => sort.key === "signUpDate" && sort.direction === "desc";

export const getCustomerApiSort = (
  sort: DashboardTableColumnSort<CustomerTableSortKey>,
): ShopifySalesProductCustomersSort => {
  const suffix = sort.direction === "asc" ? "asc" : "desc";
  if (sort.key === "totalOrders") {
    return `total_orders_${suffix}`;
  }
  if (sort.key === "totalSpent") {
    return `total_spend_${suffix}`;
  }
  if (sort.key === "recentOrderDate") {
    return `recent_order_date_${suffix}`;
  }
  return `sign_up_date_${suffix}`;
};

const getCustomerSortAccessors = () => ({
  totalOrders: (customer: SalesCustomer) => customer.totalOrders,
  totalSpent: (customer: SalesCustomer) => customer.totalSpent,
  recentOrderDate: (customer: SalesCustomer) => customer.recentOrderDate,
  signUpDate: (customer: SalesCustomer) => customer.signUpDate,
});

export const sortCustomerRows = (
  rows: readonly SalesCustomer[],
  sort: DashboardTableColumnSort<CustomerTableSortKey>,
) => {
  const sortedRows = sortRowsByKeyAccessor(rows, sort, getCustomerSortAccessors());
  return sort.key === "signUpDate" && sort.direction === "desc"
    ? sortedRows.sort((a, b) => b.signUpDate.localeCompare(a.signUpDate) || b.recentOrderDate.localeCompare(a.recentOrderDate))
    : sortedRows;
};

export const isIsoDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value.trim());

export const formatTrendAxisDate = (value: string | number) => {
  const raw = String(value);
  if (!isIsoDate(raw)) {return raw;}
  const [year, month, day] = raw.split("-");
  return `${day}-${month}-${year}`;
};

export const formatTrendTooltipDate = (value: string) => {
  if (!isIsoDate(value)) {return value;}
  const [year, month, day] = value.split("-");
  return `${day}-${month}-${year}`;
};

const CUSTOMER_EXPORT_COLUMNS = [
  "name",
  "email",
  "phone",
  "segment",
  "totalOrders",
  "totalSpend",
  "recentOrderDate",
  "signUpDate",
] as const;
type CustomerExportColumn = typeof CUSTOMER_EXPORT_COLUMNS[number];
type CustomerExportRow = Record<CustomerExportColumn, string | number>;

/**
 * Export labels come from the caller so the downloaded file matches the UI language:
 * the table renders translated headers and the API's `*FormattedValue` cells, and an
 * export hardcoded to English would disagree with what is on screen.
 */
export interface CustomerExportLabels {
  headers: Record<CustomerExportColumn, string>;
  notAvailable: string;
  notOrderedYet: string;
  fileName: string;
  sheetName: string;
}

const DEFAULT_CUSTOMER_EXPORT_LABELS: CustomerExportLabels = {
  headers: {
    name: "Customer Name",
    email: "Email",
    phone: "Phone Number",
    segment: "Segment",
    totalOrders: "Total Orders",
    totalSpend: "Total Spend",
    recentOrderDate: "Recent Order Date",
    signUpDate: "Sign Up Date",
  },
  notAvailable: NOT_AVAILABLE,
  notOrderedYet: NOT_AVAILABLE,
  fileName: "customers",
  sheetName: "Customers",
};

export const customerSegmentTone = (segment: string) => {
  const normalized = segment.toLowerCase();
  if (normalized.includes("loyal")) { return "loyal"; }
  if (normalized.includes("high")) { return "high-spenders"; }
  if (normalized.includes("idle")) { return "idle"; }
  if (normalized.includes("unsubscribe")) { return "unsubscribed"; }
  if (normalized.includes("one")) { return "one-timers"; }
  if (normalized.includes("cart")) { return "cart-abandoners"; }
  if (normalized.includes("indecisive")) { return "indecisive"; }
  return "default";
};

const formatCustomerSegments = (segments: string[], notAvailable: string) =>
  segments.length ? segments.join(", ") : notAvailable;

/** Blank, or the API's own English "Not Available" sentinel, both mean "no value". */
const customerExportText = (value: string | undefined, fallback: string) => {
  const text = value?.trim();
  return !text || text === NOT_AVAILABLE ? fallback : text;
};

const customerExportRows = (
  customers: SalesCustomer[],
  labels: CustomerExportLabels,
): CustomerExportRow[] =>
  customers.map(customer => ({
    name: customerExportText(customer.name, labels.notAvailable),
    email: customerExportText(customer.email, labels.notAvailable),
    phone: customerExportText(customer.phone, labels.notAvailable),
    segment: formatCustomerSegments(customer.segments, labels.notAvailable),
    // Prefer the API's localized cells — the same ones the table renders — so exported
    // digits and currency match the dashboard instead of falling back to Latin digits.
    totalOrders: customerExportText(
      customer.totalOrdersFormattedValue,
      String(customer.totalOrders),
    ),
    totalSpend: customerExportText(
      customer.totalSpentFormattedValue,
      formatCurrency(customer.totalSpent),
    ),
    recentOrderDate: customerExportText(
      customer.recentOrderDateFormattedValue ?? customer.recentOrderDate,
      labels.notOrderedYet,
    ),
    signUpDate: customerExportText(
      customer.signUpDateFormattedValue ?? customer.signUpDate,
      labels.notAvailable,
    ),
  }));

const csvEscape = (value: unknown) => {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replaceAll("\"", "\"\"")}"` : text;
};

const csvCustomerCell = (
  column: CustomerExportColumn,
  value: string | number,
  labels: CustomerExportLabels,
) => {
  const text = String(value ?? "");
  // Phone numbers keep leading zeros only when Excel reads them as a quoted string;
  // the placeholder label must stay a plain cell.
  return column === "phone" && text !== labels.notAvailable
    ? csvEscape(`="${text.replaceAll("\"", "\"\"")}"`)
    : csvEscape(text);
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const exportCustomersCsv = (
  customers: SalesCustomer[],
  labels: CustomerExportLabels = DEFAULT_CUSTOMER_EXPORT_LABELS,
) => {
  const rows = customerExportRows(customers, labels);
  const lines = [
    CUSTOMER_EXPORT_COLUMNS.map(column => csvEscape(labels.headers[column])).join(","),
    ...rows.map(row =>
      CUSTOMER_EXPORT_COLUMNS.map(column => csvCustomerCell(column, row[column], labels)).join(","),
    ),
  ];
  downloadBlob(
    // The BOM makes Excel read the file as UTF-8 — without it Arabic headers and values
    // open as mojibake.
    new Blob(["\uFEFF", lines.join("\n")], { type: "text/csv;charset=utf-8;" }),
    `${labels.fileName}_${dayjs().format("YYYY-MM-DD")}.csv`,
  );
};

/** Excel rejects the characters below in sheet names and truncates past 31 chars. */
const EXCEL_SHEET_NAME_FORBIDDEN = new Set(["[", "]", ":", "*", "?", "/", "\\"]);

const excelSheetName = (name: string) => {
  const cleaned = Array.from(name)
    .map(character => (EXCEL_SHEET_NAME_FORBIDDEN.has(character) ? " " : character))
    .join("")
    .trim();
  return (cleaned || "Sheet1").slice(0, 31);
};

const xmlEscape = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

const crc32 = (data: Uint8Array) => {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    c = crcTable[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
};

const writeUInt16 = (buffer: Uint8Array, offset: number, value: number) => {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = (value >>> 8) & 0xff;
};

const writeUInt32 = (buffer: Uint8Array, offset: number, value: number) => {
  buffer[offset] = value & 0xff;
  buffer[offset + 1] = (value >>> 8) & 0xff;
  buffer[offset + 2] = (value >>> 16) & 0xff;
  buffer[offset + 3] = (value >>> 24) & 0xff;
};

const concatUint8 = (parts: Uint8Array[]) => {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const output = new Uint8Array(total);
  let offset = 0;
  parts.forEach(part => {
    output.set(part, offset);
    offset += part.length;
  });
  return output;
};

const createZip = (files: Array<{ name: string; content: string }>) => {
  const encoder = new TextEncoder();
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  files.forEach(file => {
    const nameBytes = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const checksum = crc32(data);
    const local = new Uint8Array(30 + nameBytes.length);
    writeUInt32(local, 0, 0x04034b50);
    writeUInt16(local, 4, 20);
    writeUInt16(local, 6, 0);
    writeUInt16(local, 8, 0);
    writeUInt32(local, 10, 0);
    writeUInt32(local, 14, checksum);
    writeUInt32(local, 18, data.length);
    writeUInt32(local, 22, data.length);
    writeUInt16(local, 26, nameBytes.length);
    writeUInt16(local, 28, 0);
    local.set(nameBytes, 30);
    localParts.push(local, data);

    const central = new Uint8Array(46 + nameBytes.length);
    writeUInt32(central, 0, 0x02014b50);
    writeUInt16(central, 4, 20);
    writeUInt16(central, 6, 20);
    writeUInt16(central, 8, 0);
    writeUInt16(central, 10, 0);
    writeUInt32(central, 12, 0);
    writeUInt32(central, 16, checksum);
    writeUInt32(central, 20, data.length);
    writeUInt32(central, 24, data.length);
    writeUInt16(central, 28, nameBytes.length);
    writeUInt16(central, 30, 0);
    writeUInt16(central, 32, 0);
    writeUInt16(central, 34, 0);
    writeUInt16(central, 36, 0);
    writeUInt32(central, 38, 0);
    writeUInt32(central, 42, offset);
    central.set(nameBytes, 46);
    centralParts.push(central);
    offset += local.length + data.length;
  });

  const centralDirectory = concatUint8(centralParts);
  const end = new Uint8Array(22);
  writeUInt32(end, 0, 0x06054b50);
  writeUInt16(end, 8, files.length);
  writeUInt16(end, 10, files.length);
  writeUInt32(end, 12, centralDirectory.length);
  writeUInt32(end, 16, offset);
  return concatUint8([...localParts, centralDirectory, end]);
};

export const exportCustomersXlsx = (
  customers: SalesCustomer[],
  labels: CustomerExportLabels = DEFAULT_CUSTOMER_EXPORT_LABELS,
) => {
  const rows = customerExportRows(customers, labels);
  const sheetRows: Array<ReadonlyArray<string | number>> = [
    CUSTOMER_EXPORT_COLUMNS.map(column => labels.headers[column]),
    ...rows.map(row => CUSTOMER_EXPORT_COLUMNS.map(column => row[column])),
  ];
  const sheetData = sheetRows
    .map((row, rowIndex) => (
      `<row r="${rowIndex + 1}">${row
        .map((cell, columnIndex) => {
          const ref = `${String.fromCodePoint(65 + columnIndex)}${rowIndex + 1}`;
          return `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(cell)}</t></is></c>`;
        })
        .join("")}</row>`
    ))
    .join("");
  const workbook = createZip([
    {
      name: "[Content_Types].xml",
      content:
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>" +
        "<Types xmlns=\"http://schemas.openxmlformats.org/package/2006/content-types\">" +
        "<Default Extension=\"rels\" ContentType=\"application/vnd.openxmlformats-package.relationships+xml\"/>" +
        "<Default Extension=\"xml\" ContentType=\"application/xml\"/>" +
        "<Override PartName=\"/xl/workbook.xml\" ContentType=\"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml\"/>" +
        "<Override PartName=\"/xl/worksheets/sheet1.xml\" ContentType=\"application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml\"/>" +
        "</Types>",
    },
    {
      name: "_rels/.rels",
      content:
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>" +
        "<Relationships xmlns=\"http://schemas.openxmlformats.org/package/2006/relationships\">" +
        "<Relationship Id=\"rId1\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument\" Target=\"xl/workbook.xml\"/>" +
        "</Relationships>",
    },
    {
      name: "xl/workbook.xml",
      content:
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>" +
        "<workbook xmlns=\"http://schemas.openxmlformats.org/spreadsheetml/2006/main\" xmlns:r=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships\">" +
        `<sheets><sheet name="${xmlEscape(excelSheetName(labels.sheetName))}" sheetId="1" r:id="rId1"/></sheets></workbook>`,
    },
    {
      name: "xl/_rels/workbook.xml.rels",
      content:
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>" +
        "<Relationships xmlns=\"http://schemas.openxmlformats.org/package/2006/relationships\">" +
        "<Relationship Id=\"rId1\" Type=\"http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet\" Target=\"worksheets/sheet1.xml\"/>" +
        "</Relationships>",
    },
    {
      name: "xl/worksheets/sheet1.xml",
      content:
        "<?xml version=\"1.0\" encoding=\"UTF-8\"?>" +
        "<worksheet xmlns=\"http://schemas.openxmlformats.org/spreadsheetml/2006/main\">" +
        `<sheetData>${sheetData}</sheetData></worksheet>`,
    },
  ]);

  downloadBlob(
    new Blob([workbook], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${labels.fileName}_${dayjs().format("YYYY-MM-DD")}.xlsx`,
  );
};

export const SALES_PRODUCT_METRIC_TOOLTIPS: Record<string, DefinitionTooltipInfo> = {
  Revenue: {
    description:
      "Total Sales generated from orders placed within the selected date range.",
    formula: TOTAL_SALES_METRIC_FORMULA,
  },
  "Net Revenue": {
    description:
      "Total Sales generated from orders placed within the selected date range.",
    formula: TOTAL_SALES_METRIC_FORMULA,
  },
  "Total Sales": {
    description:
      "Total sales generated from orders placed within the selected date range.",
    formula:
      "Total Sales = Gross Sales - Discounts - Returns + Additional Fees + Duties + Shipping Charges + Taxes",
  },
  "Total sales": {
    description:
      "Total sales generated from orders placed within the selected date range.",
    formula:
      "Total Sales = Gross Sales - Discounts - Returns + Additional Fees + Duties + Shipping Charges + Taxes",
  },
  Orders: {
    description: "Number of orders placed during the selected date range.",
    formula: "Orders = Count of orders placed during selected date range",
  },
  "Total Orders by Status": {
    description:
      "Shows the total number of orders during the selected date range, broken down by order fulfillment status including Fulfilled, Unfulfilled, and Cancelled orders.",
    formula:
      "Total Orders by Status = Count of orders grouped by fulfillment status within the selected date range.",
  },
  "Top Channel": {
    description:
      "Shows the sales channel with the highest activity for the selected date range.",
    formula:
      "Top Channel = Sales channel ranked first by selected sales/channel metric",
  },
  Customers: {
    description: "Number of customers who placed at least one order during the selected date range.",
    formula: "Customers Ordered = Customers who ordered once during selected date range",
  },
  "Customers In Period": {
    description:
      "New customers registered during the selected date range.",
    formula:
      "Customers in Period = New customers registered in selected date range",
  },
  "Avg. order value": {
    description:
      "Average amount spent per order during the selected date range, before returns are applied.",
    formula: "Average Order Value = (Gross Sales - Discounts) / Orders",
  },
  Inventory: {
    description: "Estimated value of on-hand inventory using unit cost.",
    formula: "Sum of (stock × cost) per SKU",
  },
  "Avg. CLTV": {
    description: "Shows the average total value a customer is expected to generate over their relationship with the business.",
    formula: "Average CLTV = Total revenue generated by all customers ÷ Total number of customers",
  },
  "Total Products": {
    description: "Number of products available in the Zid catalog.",
    formula: "Total Products = Number of products in your store",
  },
  "In Stock": {
    description:
      "Products currently available for purchase with inventory above the configured low-stock threshold.",
    formula:
      "In Stock = Count of products/variants where inventory quantity > low-stock threshold",
  },
  "Low Stock": {
    description:
      "Products with remaining inventory at or below the configured low-stock threshold and above zero.",
    formula:
      "Low Stock = Count of products/variants where inventory quantity > 0 and <= low-stock threshold",
  },
  "Out of Stock": {
    description: "Products with no available inventory.",
    formula: "Out of Stock = Count of products/variants where inventory quantity <= 0",
  },
  "Inventory value": {
    description: "Shows the total number of inventory units currently on hand across all products.",
    formula: "Inventory Units = Sum of all stock quantities across active products",
  },
  "Total Customers": {
    description: "Shows the total number of unique customers who have made a purchase.",
    formula: "Total Customers = Count of unique customers",
  },
  "Customers (All-Time)": {
    description: "The total number of unique customers who have ever made a purchase.",
    formula: "Customers (All-Time) = Total count of unique customers",
  },
  "New Customers": {
    description: "Customers whose first order was placed during the selected date range.",
    formula: "New Customers = Count of customers with first order date within selected date range",
  },
  "Avg. Order Value": {
    description:
      "Average amount spent per order during the selected date range, before returns are applied.",
    formula: "Average Order Value = (Gross Sales - Discounts) / Orders",
  },
  "Net Profit": {
    description: "Shows the actual profit after subtracting all costs, expenses, taxes, and refunds from revenue.",
    formula: "Net Profit = Revenue − Shipping Costs − Additional Fees − Duties − Refunds",
  },
  "Total Taxes": {
    description: "Shows the total tax amount collected from all orders.",
    formula: "Total Taxes = Sum of all tax amounts",
  },
  Refunds: {
    description:
      "Value removed from orders through refunds during the selected date range.",
    formula: "Returns = Value removed from orders through refunds",
  },
  "Customers Ordered": {
    description:
      "Customers who placed at least one order during the selected date range.",
    formula: "Customers Ordered = Customer who ordered once during selected date range",
  },
  "Total Spend": {
    description:
      "Total amount spent by the customers shown in the table.",
    formula:
      "Total Spend = Sum of customer order value based on applied table scope",
  },
  Segment: {
    description:
      "DataTram-calculated customer segment based on the customer's latest purchase behavior, engagement pattern, and customer history. A customer may have one or more segments depending on their activity.",
    formula:
      "Customer Segment = DataTram-calculated classification using customer orders, total spend, purchase frequency and recency.",
  },
  "Total Orders": {
    description:
      "Total number of valid orders placed by this customer during the applicable reporting period or customer history window.",
  },
  "Recent Order Date": {
    description:
      "Date of the customer's most recent valid order. \"Not Ordered Yet\" is displayed when the customer has not placed an order.",
  },
  "Sign Up Date": {
    description:
      "Date the customer profile was first created in your connected ecommerce store.",
  },
};

const SALES_PRODUCT_SECTION_TOOLTIPS: Record<string, DefinitionTooltipInfo> = {
  "Total Sales Trend by Day": {
    description:
      "Shows how Total Sales changed day by day across the selected date range.",
    formula: SALES_TREND_FORMULA,
  },
  "Total Sales Trend": {
    description:
      "Shows the selected Total Sales metric over time for the dashboard date range.",
    formula: SALES_TREND_FORMULA,
  },
  "Sales Trend": {
    description:
      "Shows the selected Total Sales metric over time for the dashboard date range.",
    formula: SALES_TREND_FORMULA,
  },
  "Total Sales By Channel": {
    description:
      "Shows Total Sales distributed across order referrer channels for the selected date range",
formula: TOTAL_SALES_BY_CHANNEL_FORMULA,
  },
  "Channel Distribution": {
    description:
      "Shows how the selected-period Total Sales metric is distributed across sales channels.",
    formula: TOTAL_SALES_BY_CHANNEL_FORMULA,
  },
  "Top 10 Products by Orders": {
    description:
      "Products ranked by the number of orders they appeared in during the selected date range.",
    formula:
      "Top Products by Orders = Products ranked by total number of orders in selected date range",
  },
  "Top 10 Products by Gross Sales": {
    description:
      "Products ranked by gross sales generated during the selected date range. Gross sales are calculated at the product level before discounts, returns, shipping, taxes, duties, and additional order-level charges are applied.",
    formula:
      "Product Gross Sales = Sum of product line item price × quantity sold, grouped by product ID",
  },
  "Customer Breakdown": {
    description:
      "Shows how customers are split between new and returning based on their purchase behavior.",
  },
  "Financial Summary": {
    description:
      "Provides an overview of key financial metrics including revenue, costs, and profit.",
  },
  "Sales by Channel": {
    description:
      "Shows Total Sales generated from each sales channel.",
    formula: TOTAL_SALES_BY_CHANNEL_FORMULA,
  },
  "Orders by Channel": {
    description:
      "Shows total Orders placed across each sales channel.",
    formula: "Channel Distribution = Total Orders grouped by Sales Channel",
  },
  Orders: {
    description:
      "Displays detailed information for each order including customer, channel, amount, status, and date.",
  },
  "Stock Comparison": {
    description:
      "Shows the top 10 product variants by current on-hand inventory quantity. Snapshot not filtered by order date.",
    formula: "On-hand Quantity = Current Zid inventory quantity for each product variant",
  },
  "Top 10 Product by On-hand Quantity": {
    description:
      "Shows the top 10 product by current on-hand inventory quantity. Snapshot not filtered by order date.",
    formula: "On-hand Quantity = Current Zid inventory quantity for each product",
  },
  Products: {
    description:
      "Product inventory detail including category, stock quantity, and inventory status.",
    formula:
      "Status = In Stock, Low Stock, or Out of Stock based on current inventory quantity and configured threshold",
  },
  "New vs Returning Customers": {
    description:
      "Shows the split of customers who ordered in the selected date range based on whether this was their first order or a repeat order.",
    formula: "Total Customers = New Customers + Returning Customers",
  },
  "All Customers": {
    description:
      "Shows customers who signed up during the selected date range, along with their engagement metrics such as orders, spend, segment, and recent order activity.",
    formula:
      "Customers shown = Customers whose sign-up date falls within the selected date range.",
  },
  "Monthly P&L": {
    description:
      "Shows monthly trends of revenue, expenses, and profit over time.",
  },
  "Revenue by Channel": {
    description:
      "Shows how total revenue is distributed across different sales channels (e.g., online store, retail).",
    formula: "Revenue per Channel = Sum of order values for each sales channel",
  },
  "Tax Breakdown": {
    description:
      "Shows how total taxes are distributed across different tax types (e.g., sales tax, VAT, others).",
  },
  "Financial Breakdown": {
    description:
      "Shows a summary of key financial metrics including revenue, costs, and profitability.",
  },
};

const normalizeTooltipLookupKey = (value: string) =>
  value.trim().toLowerCase().replace(/\s+/g, " ");

export const getMetricTooltip = (label: string) =>
  SALES_PRODUCT_METRIC_TOOLTIPS[label] ??
  SALES_PRODUCT_METRIC_TOOLTIPS[
  Object.keys(SALES_PRODUCT_METRIC_TOOLTIPS).find(
    key => normalizeTooltipLookupKey(key) === normalizeTooltipLookupKey(label),
  ) ?? ""
  ];

/**
 * `SALES_PRODUCT_METRIC_TOOLTIPS`/`SALES_PRODUCT_SECTION_TOOLTIPS` are static English
 * dictionaries keyed by the same English string used for icon/tooltip lookups elsewhere,
 * so they can't be rewritten in place without breaking those lookups. This maps a handful
 * of Overview-tab keys to i18n keys and overlays the translated `description`/`formula`
 * on top of the English entry (still returning the English one untouched for any key not
 * yet in this map).
 */
const METRIC_TOOLTIP_I18N_KEYS: Record<string, string> = {
  "total sales": "salesProduct.overview.tooltips.totalSales",
  "revenue": "salesProduct.overview.tooltips.totalSales",
  "refunds": "salesProduct.overview.tooltips.refunds",
  "orders": "salesProduct.overview.tooltips.orders",
  "avg. order value": "salesProduct.overview.tooltips.avgOrderValue",
  "customers ordered": "salesProduct.overview.tooltips.customersOrdered",
  "total orders by status": "salesProduct.sales.tooltips.totalOrdersByStatus",
  "total products": "salesProduct.inventory.tooltips.totalProducts",
  "in stock": "salesProduct.inventory.tooltips.inStock",
  "low stock": "salesProduct.inventory.tooltips.lowStock",
  "customers in period": "salesProduct.customers.tooltips.customersInPeriod",
  "segment": "salesProduct.customers.tooltips.segment",
  "total orders": "salesProduct.customers.tooltips.totalOrders",
  "total spend": "salesProduct.customers.tooltips.totalSpend",
  "recent order date": "salesProduct.customers.tooltips.recentOrderDate",
  "sign up date": "salesProduct.customers.tooltips.signUpDate",
};
const SECTION_TOOLTIP_I18N_KEYS: Record<string, string> = {
  "total sales trend": "salesProduct.overview.sectionTooltips.totalSalesTrend",
  "total sales by channel": "salesProduct.overview.sectionTooltips.totalSalesByChannel",
  "top 10 products by orders": "salesProduct.overview.sectionTooltips.topProductsByOrders",
  "top 10 products by gross sales": "salesProduct.overview.sectionTooltips.topProductsByRevenue",
  "sales by channel": "salesProduct.sales.sectionTooltips.salesByChannel",
  "orders by channel": "salesProduct.sales.sectionTooltips.ordersByChannel",
  "orders": "salesProduct.sales.sectionTooltips.orders",
  "top 10 product by on-hand quantity": "salesProduct.inventory.sectionTooltips.topStockProducts",
  "products": "salesProduct.inventory.sectionTooltips.products",
  "new vs returning customers": "salesProduct.customers.sectionTooltips.newVsReturning",
  "all customers": "salesProduct.customers.sectionTooltips.allCustomers",
};

function useLocalizedTooltipInfo(
  lookupKey: string,
  info: DefinitionTooltipInfo | undefined,
  i18nKeyMap: Record<string, string>,
): DefinitionTooltipInfo | undefined {
  const { t } = useTranslation();
  if (!info) {
    return info;
  }
  const i18nKey = i18nKeyMap[normalizeTooltipLookupKey(lookupKey)];
  if (!i18nKey) {
    return info;
  }
  return {
    ...info,
    description: t(`${i18nKey}.description`, { defaultValue: info.description }),
    ...(info.formula
      ? { formula: t(`${i18nKey}.formula`, { defaultValue: info.formula }) }
      : {}),
  };
}

/** Overlays a translated `description`/`formula` on a `SALES_PRODUCT_METRIC_TOOLTIPS` entry for known keys. */
export function useLocalizedMetricTooltip(
  label: string,
  info: DefinitionTooltipInfo | undefined,
): DefinitionTooltipInfo | undefined {
  return useLocalizedTooltipInfo(label, info, METRIC_TOOLTIP_I18N_KEYS);
}


export const LocalizedMetricTooltip: FC<{
  label: string;
  align?: "start" | "end";
  preferSide?: "left" | "right";
}> = ({ label, align, preferSide }) => {
  const { t } = useTranslation();
  const info = useLocalizedMetricTooltip(label, getMetricTooltip(label));
  if (!info) {
    return null;
  }
  return (
    <DefinitionTooltip
      label={label}
      info={info}
      align={align}
      preferSide={preferSide}
      formulaTitle={t("salesProduct.shared.formula")}
      exampleLabel={t("salesProduct.shared.example")}
    />
  );
};

export const formatKpiChangePercent = (
  value: number | null | undefined,
  isRTL: boolean,
): string | undefined => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  const magnitude = Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: value === 0 ? 1 : 0,
    maximumFractionDigits: 2,
  });
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return localizeChartDigits(`${sign}${magnitude}%`, isRTL);
};

export const useComparisonLabelLocalizer = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  return useCallback(
    (label: string | undefined) => {
      if (!label) {
        return label;
      }
      const vsWord = t("salesProduct.overview.vsLabel", { defaultValue: "vs" });
      const withTranslatedVs = label.replace(/^vs\b/i, vsWord);
      const match = withTranslatedVs.match(new RegExp(`^${vsWord}\\s+(.+?)\\s+(→|->)\\s+(.+)$`));
      if (!match) {
        return withTranslatedVs;
      }
      const [, from, arrow, to] = match;
      return `${vsWord} ${localizeChartDigits(from, isRTL)} ${arrow} ${localizeChartDigits(to, isRTL)}`;
    },
    [isRTL, t],
  );
};

export const SectionTitle: FC<{
  title: string;
  suffix?: string;
  align?: "start" | "end";
  /** English lookup key into `SALES_PRODUCT_SECTION_TOOLTIPS` when `title` is localized/translated text; defaults to `title`. */
  tooltipKey?: string;
  tooltipPreferSide?: "left" | "right";
}> = ({ title, suffix, align = "start", tooltipKey, tooltipPreferSide }) => {
  const { t } = useTranslation();
  const englishKey = tooltipKey ?? title;
  const tooltipInfo = useLocalizedTooltipInfo(
    englishKey,
    SALES_PRODUCT_SECTION_TOOLTIPS[englishKey],
    SECTION_TOOLTIP_I18N_KEYS,
  );
  return (
    <div className="sales-product-analytics-dashboard__title-with-tooltip">
      <h3 className="sales-product-analytics-dashboard__card-title">
        {title}
        {suffix ? ` ${suffix}` : ""}
      </h3>
      {tooltipInfo ? (
        <DefinitionTooltip
          label={englishKey}
          info={tooltipInfo}
          align={align}
          preferSide={tooltipPreferSide}
          formulaTitle={t("salesProduct.shared.formula")}
          exampleLabel={t("salesProduct.shared.example")}
        />
      ) : null}
    </div>
  );
};

export const MetricCard: FC<{
  label: string;
  /** Localized text to display instead of `label`; `label` stays the English lookup key for tooltip/icon maps. */
  displayLabel?: string;
  value: string;
  tone?: "positive" | "negative" | "neutral";
  icon?: FC<{ className?: string }>;
  changePercent?: number;
  /** Pre-formatted trend from the API, preferred over formatting `changePercent` locally. */
  changePercentFormattedValue?: string;
  comparisonLabel?: string;
  statusHint?: string;
  tooltipAlign?: "start" | "end";
  hideTrendSection?: boolean;
  trendPolarity?: "higher-is-better" | "lower-is-better";
}> = ({
  label,
  displayLabel,
  value,
  tone = "neutral",
  icon: Icon,
  changePercent,
  changePercentFormattedValue,
  comparisonLabel = "vs last period",
  statusHint,
  tooltipAlign = "start",
  hideTrendSection = false,
  trendPolarity,
}) => {
    const { t } = useTranslation();
    const tooltip = useLocalizedTooltipInfo(label, getMetricTooltip(label), METRIC_TOOLTIP_I18N_KEYS);

    return (
      <DashboardKpiCard
        title={displayLabel ?? label}
        value={value}
        metricLabelForTooltip={label}
        tooltip={tooltip}
        tooltipAlign={tooltipAlign}
        formulaTitle={t("salesProduct.shared.formula")}
        icon={
          Icon ? (
            <Icon className="sales-product-analytics-dashboard__metric-icon-svg" />
          ) : undefined
        }
        changePercent={changePercent}
        changePercentFormattedValue={changePercentFormattedValue}
        comparisonLabel={comparisonLabel}
        description={statusHint ?? undefined}
        hideNoTrendFallback={hideTrendSection}
        noTrendLabel={t("salesProduct.shared.trendUnavailable")}
        noTrendTooltip={t("salesProduct.shared.trendUnavailableInfo")}
        tone={tone}
        trendPolarity={trendPolarity}
      />
    );
  };

/** Below this count, a single column reads better than two half-empty ones. */
export const OTHERS_TOOLTIP_TWO_COLUMN_MIN = 3;

/** The two-column Others list reads its row count off this CSS variable (see .scss). */
interface OthersTooltipListStyle extends CSSProperties {
  "--others-tooltip-rows"?: number;
}

export const ChannelBarTooltip: FC<{
  point: ChannelBarPoint;
  currencyLabel: string;
}> = ({ point, currencyLabel }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const othersBreakdown = point.label === "Others" ? point.othersItems ?? [] : [];

  if (othersBreakdown.length > 0) {
    const useTwoColumns = othersBreakdown.length >= OTHERS_TOOLTIP_TWO_COLUMN_MIN;
    const listStyle: OthersTooltipListStyle | undefined = useTwoColumns
      ? { "--others-tooltip-rows": Math.ceil(othersBreakdown.length / 2) }
      : undefined;

    return (
      <div className="sales-product-analytics-dashboard__channel-tooltip sales-product-analytics-dashboard__channel-tooltip--compact">
        <ul
          className={`sales-product-analytics-dashboard__channel-tooltip-others-list${
            useTwoColumns
              ? " sales-product-analytics-dashboard__channel-tooltip-others-list--columns"
              : ""
          }`}
          style={listStyle}
        >
          {othersBreakdown.map((item, index) => {
            const calculateShare = () => {
              if (Number.isFinite(item.sharePercent)) {
                return Math.min(100, Math.max(0, item.sharePercent ?? 0));
              }
              if (point.totalSales > 0) {
                return channelSharePercent(item.value, point.totalSales);
              }
              return 0;
            };
            const share = calculateShare();
            return (
              <li
                key={`${item.label}-${index}`}
                className="sales-product-analytics-dashboard__channel-tooltip-others-row"
              >
                <span className="sales-product-analytics-dashboard__channel-tooltip-others-name">
                  {item.label}
                </span>
                <span
                  className="sales-product-analytics-dashboard__channel-tooltip-others-swatch"
                  style={{ backgroundColor: channelDistributionBarColor(index) }}
                  aria-hidden
                />
                <span className="sales-product-analytics-dashboard__channel-tooltip-others-pct">
                  {localizeChartDigits(formatChannelPercent(share), isRTL)}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="sales-product-analytics-dashboard__channel-tooltip sales-product-analytics-dashboard__channel-tooltip--compact">
      <div className="sales-product-analytics-dashboard__channel-tooltip-single-row">
        <span className="sales-product-analytics-dashboard__channel-tooltip-others-name">
          {point.label}
        </span>
        <span
          className="sales-product-analytics-dashboard__channel-tooltip-others-swatch"
          style={{ backgroundColor: point.color }}
          aria-hidden
        />
        <span className="sales-product-analytics-dashboard__channel-tooltip-others-pct">
          {localizeChartDigits(formatChannelPercent(point.percent), isRTL)}
        </span>
      </div>
      <div className="sales-product-analytics-dashboard__channel-tooltip-single-sales">
        <span>{t("salesProduct.overview.kpi.totalSales", { defaultValue: TOTAL_SALES_METRIC_LABEL })}</span>
        <strong>
          {localizeChartDigits(formatCurrencyTooltipValue(point.value, currencyLabel), isRTL)}
        </strong>
      </div>
    </div>
  );
};

export const InventoryStockTooltip: FC<{
  active?: boolean;
  payload?: Array<{ value?: number; payload?: { fullName?: string } }>;
}> = ({ active, payload }) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0];
  return (
    <div className="chart-tooltip-container">
      <div className="chart-tooltip-label">{row.payload?.fullName ?? t("salesProduct.shared.productVariant")}</div>
      <div className="chart-tooltip-value">
        <span className="chart-tooltip-title">{t("salesProduct.shared.onHandQuantity")}:</span>
        <span className="chart-tooltip-number">
          {localizeChartDigits(formatNumberTooltipValue(Number(row.value ?? 0)), isRTL)}
        </span>
      </div>
    </div>
  );
};

export const InventoryBarValueLabel: FC<{
  viewBox?: { x?: number; y?: number; width?: number; height?: number };
  value?: number | string;
  isRTL?: boolean;
}> = ({ viewBox, value, isRTL = false }) => {
  const { x = 0, y = 0, width = 0, height = 0 } = viewBox ?? {};
  const tipX = x + width;
  return (
    <text
      x={isRTL ? tipX - 8 : tipX + 8}
      y={y + height / 2}
      textAnchor={isRTL ? "end" : "start"}
      dominantBaseline="central"
      className="sales-product-analytics-dashboard__bar-label"
    >
      {localizeChartDigits(formatNumberTooltipValue(Number(value ?? 0)), isRTL)}
    </text>
  );
};

export const InventoryYAxisTick: FC<{
  x?: number;
  y?: number;
  payload?: { value?: string };
  isRTL?: boolean;
}> = ({ x = 0, y = 0, payload, isRTL = false }) => (
  <text
    x={isRTL ? x : 0}
    y={y}
    dy={4}
    className="sales-product-analytics-dashboard__inventory-y-axis-tick"
  >
    {payload?.value ?? ""}
  </text>
);

export const InventoryEmptyState: FC<{ message?: string }> = ({
  message = "No products match the current search and filters.",
}) => (
  <div className="sales-product-analytics-dashboard__inventory-empty-state">
    <span className="sales-product-analytics-dashboard__inventory-empty-icon" aria-hidden>
      <SearchX size={24} />
    </span>
    <span>{message}</span>
  </div>
);

export const InventoryProductImage: FC<{ imageUrl?: string }> = ({ imageUrl }) => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(imageUrl && !failed);
  const unavailableLabel = t("salesProduct.shared.productImageUnavailable");

  return (
    <span
      className={`sales-product-analytics-dashboard__product-image-frame ${showImage ? "" : "sales-product-analytics-dashboard__product-image-frame--unavailable"}`.trim()}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span
          className="sales-product-analytics-dashboard__product-image-unavailable"
          aria-label={unavailableLabel}
        >
          <span className="sales-product-analytics-dashboard__product-image-question" aria-hidden>
            ?
          </span>
          <span className="sales-product-analytics-dashboard__product-image-tooltip" role="tooltip">
            {unavailableLabel}
          </span>
        </span>
      )}
    </span>
  );
};

export const LowStockThresholdControl: FC<{
  value: number;
  inputValue: string;
  isAdmin: boolean;
  saving: boolean;
  confirmOpen: boolean;
  onInputChange: (_value: string) => void;
  onCommitRequest: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({
  value,
  inputValue,
  isAdmin,
  saving,
  confirmOpen,
  onInputChange,
  onCommitRequest,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const parsedInputValue = Number(inputValue);
  const canCommitThreshold =
    isAdmin &&
    !saving &&
    !confirmOpen &&
    inputValue.trim().length > 0 &&
    Number.isFinite(parsedInputValue) &&
    Number.isInteger(parsedInputValue) &&
    parsedInputValue !== value;

  return (
    <>
      {confirmOpen ? <div className="sales-product-analytics-dashboard__threshold-overlay" /> : null}
      <div className="sales-product-analytics-dashboard__threshold-control">
        <label htmlFor="inventory-low-stock-threshold">{t("salesProduct.shared.lowStockThresholdLabel")}</label>
        <div
          className={`sales-product-analytics-dashboard__threshold-input-wrap ${
            !isAdmin ? "sales-product-analytics-dashboard__threshold-input-wrap--disabled" : ""
          }`.trim()}
        >
          <input
            id="inventory-low-stock-threshold"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            pattern="[0-9]*"
            value={isAdmin ? inputValue : String(value)}
            disabled={!isAdmin || saving}
            onChange={event => onInputChange(event.target.value.replace(/\D/g, ""))}
            onBlur={onCommitRequest}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.currentTarget.blur();
              }
            }}
          />
          <button
            type="button"
            className="sales-product-analytics-dashboard__threshold-commit-button"
            aria-label={t("salesProduct.shared.applyLowStockThreshold")}
            title={t("salesProduct.shared.applyThreshold")}
            disabled={!canCommitThreshold}
            onMouseDown={event => event.preventDefault()}
            onClick={onCommitRequest}
          >
            <Check size={16} />
          </button>
          {!isAdmin ? (
            <div className="sales-product-analytics-dashboard__threshold-disabled-tooltip" role="tooltip">
              {t("salesProduct.shared.thresholdAdminOnly")}
            </div>
          ) : null}
        </div>

        {confirmOpen ? (
          <dialog className="sales-product-analytics-dashboard__threshold-confirm" open>
            <p>{t("salesProduct.shared.thresholdConfirmMessage")}</p>
            <div>
              <button type="button" onClick={onCancel} disabled={saving}>
                {t("salesProduct.shared.no")}
              </button>
              <button type="button" onClick={onConfirm} disabled={saving}>
                {t("salesProduct.shared.yes")}
              </button>
            </div>
          </dialog>
        ) : null}
      </div>
    </>
  );
};

export const SelectMenu: FC<{
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (_value: string) => void;
  className?: string;
  dropdownClassName?: string;
  ariaLabel?: string;
  openUpward?: boolean;
  disabled?: boolean;
}> = ({ value, options, onChange, className = "", dropdownClassName = "", ariaLabel, openUpward = false, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [scrollThumbStyle, setScrollThumbStyle] = useState<CSSProperties>({ opacity: 0 });
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(option => option.value === value) ?? options[0] ?? { value: "", label: "" };
  const alwaysShowInventoryScrollbar = dropdownClassName.includes("sales-product-analytics-dashboard__select-dropdown--inventory-categories");

  const updateDropdownPosition = useCallback(() => {
    if (!rootRef.current) { return; }
    const rect = rootRef.current.getBoundingClientRect();
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    if (rect.bottom < 0 || rect.top > viewportHeight) {
      setOpen(false);
      return;
    }

    const gap = 8;
    const maxDropdownHeight = 240;
    const style: CSSProperties = {
      position: "fixed",
      left: rect.left,
      width: Math.max(rect.width, 160),
      zIndex: 2000,
      overflow: "hidden",
    };

    if (openUpward) {
      const spaceAbove = rect.top - gap;
      const maxHeight = Math.min(maxDropdownHeight, Math.max(spaceAbove - gap, 120));
      style.maxHeight = maxHeight;
      style.top = Math.max(gap, rect.top - maxHeight - gap);
    } else {
      style.maxHeight = Math.min(maxDropdownHeight, viewportHeight - rect.bottom - gap);
      style.top = rect.bottom + gap;
    }

    setDropdownStyle(style);
  }, [openUpward]);

  const updateScrollThumb = useCallback(() => {
    if (!alwaysShowInventoryScrollbar) {
      setScrollThumbStyle({ opacity: 0 });
      return;
    }

    const viewport = scrollViewportRef.current;
    if (!viewport) { return; }

    const { clientHeight, scrollHeight, scrollTop } = viewport;
    if (scrollHeight <= clientHeight + 1 || clientHeight <= 0) {
      setScrollThumbStyle({ opacity: 0 });
      return;
    }

    const trackInset = 2;
    const trackHeight = Math.max(clientHeight - (trackInset * 2), 0);
    const thumbHeight = Math.max((clientHeight / scrollHeight) * trackHeight, 24);
    const maxThumbTravel = Math.max(trackHeight - thumbHeight, 0);
    const scrollRange = Math.max(scrollHeight - clientHeight, 1);
    const thumbTop = trackInset + ((scrollTop / scrollRange) * maxThumbTravel);

    setScrollThumbStyle({
      height: thumbHeight,
      transform: `translateY(${thumbTop}px)`,
      opacity: 1,
    });
  }, [alwaysShowInventoryScrollbar]);

  useLayoutEffect(() => {
    if (!open) { return; }
    updateDropdownPosition();
    updateScrollThumb();
    const frame = window.requestAnimationFrame(() => {
      updateDropdownPosition();
      updateScrollThumb();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, updateDropdownPosition, updateScrollThumb, options.length]);

  useEffect(() => {
    if (!open) { return; }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) {
        return;
      }
      if (
        !dropdownRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) { return; }
      updateDropdownPosition();
    };
    const handleViewportScroll = () => {
      updateScrollThumb();
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("resize", updateScrollThumb);
    scrollViewportRef.current?.addEventListener("scroll", handleViewportScroll);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("resize", updateScrollThumb);
      scrollViewportRef.current?.removeEventListener("scroll", handleViewportScroll);
    };
  }, [open, updateDropdownPosition, updateScrollThumb]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const handleToggle = () => {
    if (disabled) {
      return;
    }
    if (open) {
      setOpen(false);
      return;
    }
    updateDropdownPosition();
    setOpen(true);
  };

  return (
    <div
      ref={rootRef}
      className={`sales-product-analytics-dashboard__select-menu ${className}`.trim()}
    >
      <button
        type="button"
        className={`sales-product-analytics-dashboard__select-trigger ${open ? "sales-product-analytics-dashboard__select-trigger--open" : ""} ${disabled ? "sales-product-analytics-dashboard__select-trigger--disabled" : ""}`.trim()}
        onClick={handleToggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span>{selected.label}</span>
        <ChevronDown className="sales-product-analytics-dashboard__select-trigger-icon" />
      </button>
      {open && typeof document !== "undefined" ? createPortal(
        <div
          ref={dropdownRef}
          className={`sales-product-analytics-dashboard__select-dropdown ${dropdownClassName}`.trim()}
          style={dropdownStyle}
          role="menu"
        >
          <div
            ref={scrollViewportRef}
            className={`sales-product-analytics-dashboard__select-dropdown-scroll ${alwaysShowInventoryScrollbar ? "sales-product-analytics-dashboard__select-dropdown-scroll--inventory-categories" : ""}`.trim()}
          >
            {options.map(option => {
              const active = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  className={`sales-product-analytics-dashboard__select-option ${active ? "sales-product-analytics-dashboard__select-option--active" : ""}`}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <span className="sales-product-analytics-dashboard__select-option-check">
                    {active ? <Check size={16} /> : null}
                  </span>
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
          {alwaysShowInventoryScrollbar ? (
            <div className="sales-product-analytics-dashboard__select-dropdown-scrollbar" aria-hidden="true">
              <div
                className="sales-product-analytics-dashboard__select-dropdown-scrollbar-thumb"
                style={scrollThumbStyle}
              />
            </div>
          ) : null}
        </div>,
        document.body,
      ) : null}
    </div>
  );
};

/** Overview row hides inventory + “new customers this week”; grid must use 5 tracks (not 6). */
/** Inventory KPI row: flex + full width so Ant Spin does not shrink-wrap and leave empty space. */
const PRODUCT_TYPE_FALLBACK = "Not Available";
export const EMPTY_TOP_PRODUCTS: SalesProductOverviewTopProduct[] = [];

export const normalizeProductTypeLabel = (value: string, notAvailableLabel: string = PRODUCT_TYPE_FALLBACK) => {
  const trimmed = value.trim();
  return trimmed.length ? trimmed : notAvailableLabel;
};

export const TopProductsTable: FC<{
  title: string;
  /** English lookup key into `SALES_PRODUCT_SECTION_TOOLTIPS` when `title` is localized; defaults to `title`. */
  tooltipKey?: string;
  products: SalesProductOverviewTopProduct[];
  valueKey: "orders" | "revenue";
  valueLabel: string;
  currency: string;
  emptyLabel: string;
}> = ({ title, tooltipKey, products, valueKey, valueLabel, currency, emptyLabel }) => {
  const { t } = useTranslation();
  return (
  <DashboardDataTablePanel
    as="section"
    className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--top-products"
  >
    <DashboardDataTablePanelHeader>
      <SectionTitle title={title} tooltipKey={tooltipKey} />
    </DashboardDataTablePanelHeader>
    <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
      <DashboardDataTableTable
        className={`${dt.tableCompact} sales-product-analytics-dashboard__table sales-product-analytics-dashboard__table--overview sales-product-analytics-dashboard__table--top-products`}
      >
        <thead>
          <tr>
            <th className="sales-product-analytics-dashboard__product-image-heading">{t("salesProduct.shared.columnImage")}</th>
            <th>{t("salesProduct.shared.columnProductName")}</th>
            <th className={dt.number}>{valueLabel}</th>
            <th>{t("salesProduct.shared.columnCategory")}</th>
          </tr>
        </thead>
        <tbody>
          {products.length
            ? products.map(product => (
              <tr key={`${title}-${product.id}`}>
                <td className="sales-product-analytics-dashboard__product-image-cell">
                  <span className="sales-product-analytics-dashboard__product-image-frame">
                    {product.imageUrl ? (
                      <img
                        src={product.imageUrl}
                        alt=""
                        loading="lazy"
                        onError={event => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <Package className="sales-product-analytics-dashboard__product-image-placeholder" />
                    )}
                  </span>
                </td>
                <td>
                  <span className="sales-product-analytics-dashboard__product-name">
                    {product.name}
                  </span>
                </td>
                <td className={dt.number}>
                  {valueKey === "revenue"
                    ? product.revenueFormattedValue ?? formatCurrency(product.revenue, currency)
                    : product.ordersFormattedValue ?? product.orders.toLocaleString()}
                </td>
                <td>{normalizeProductTypeLabel(product.productType, t("salesProduct.shared.notAvailable"))}</td>
              </tr>
            ))
            : (
              <tr>
                <td colSpan={4}>{emptyLabel}</td>
              </tr>
            )}
        </tbody>
      </DashboardDataTableTable>
    </DashboardDataTableScroll>
  </DashboardDataTablePanel>
  );
};

export const INVENTORY_KPI_ROW_STYLE: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  gap: 14,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

export const TopChannelKpiCard: FC<{
  channelLabel: string;
}> = ({ channelLabel }) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <DashboardKpiCard
      title={t("salesProduct.shared.topChannel")}
      value={(
        <span
          className="sales-product-analytics-dashboard__top-channel-value"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onFocus={() => setShowTooltip(true)}
          onBlur={() => setShowTooltip(false)}
          tabIndex={0}
          aria-label={t("salesProduct.shared.topChannelAria", { channel: channelLabel })}
        >
          {channelLabel}
          {showTooltip ? (
            <span
              className="sales-product-analytics-dashboard__top-channel-tooltip"
              role="tooltip"
            >
              {TOP_CHANNEL_STATUS_DUMMY_ROWS.map(row => (
                <span
                  key={row.status}
                  className="sales-product-analytics-dashboard__top-channel-tooltip-row"
                >
                  <span
                    className="sales-product-analytics-dashboard__top-channel-tooltip-dot"
                    style={{ backgroundColor: SALES_STATUS_COLORS[row.status] }}
                    aria-hidden
                  />
                  <span className="sales-product-analytics-dashboard__top-channel-tooltip-label">
                    {t(`salesProduct.shared.status.${row.status}`, { defaultValue: row.label })}
                  </span>
                  <strong>{row.count.toLocaleString()}</strong>
                </span>
              ))}
            </span>
          ) : null}
        </span>
      )}
      icon={<TrendingUp size={22} />}
      hideNoTrendFallback
      className="sales-product-analytics-dashboard__top-channel-kpi-card"
    />
  );
};

const STATUS_TOOLTIP_MARGIN = 16;

export const TotalOrdersByStatusCard: FC<{
  totalOrders: number;
  totalOrdersFormattedValue?: string;
  breakdown: Array<{
    status: string;
    count: number;
    countFormattedValue?: string;
    percent: number;
    percentFormattedValue?: string;
  }>;
}> = ({ totalOrders, totalOrdersFormattedValue, breakdown }) => {
  const { t } = useTranslation();
  const tooltip = useLocalizedMetricTooltip("Total Orders by Status", getMetricTooltip("Total Orders by Status"));
  const [activeStatus, setActiveStatus] = useState<string | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<CSSProperties | undefined>();
  const rows = SALES_STATUS_ORDER.map(status => {
    const item = breakdown.find(row => row.status.toLowerCase() === status);
    return {
      status,
      label: t(`salesProduct.shared.status.${status}`, { defaultValue: SALES_STATUS_LABELS[status] }),
      count: item?.count ?? 0,
      countFormattedValue: item?.countFormattedValue,
      percent: item?.percent ?? 0,
      percentFormattedValue: item?.percentFormattedValue,
    };
  });
  const resolvedTotal = totalOrders || rows.reduce((sum, row) => sum + row.count, 0);
  const resolvedTotalDisplay = totalOrders ? (totalOrdersFormattedValue ?? resolvedTotal.toLocaleString()) : resolvedTotal.toLocaleString();
  const activeRow = rows.find(row => row.status === activeStatus) ?? null;

  const showTooltip = useCallback((status: string, target: HTMLElement) => {
    setActiveStatus(status);
    if (typeof window === "undefined") {
      return;
    }
    const rect = target.getBoundingClientRect();
    const tooltipWidth = Math.min(260, window.innerWidth - STATUS_TOOLTIP_MARGIN * 2);
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(
      STATUS_TOOLTIP_MARGIN,
      Math.min(centerX - tooltipWidth / 2, window.innerWidth - tooltipWidth - STATUS_TOOLTIP_MARGIN)
    );
    const arrowLeft = Math.max(16, Math.min(centerX - left, tooltipWidth - 16));

    setTooltipStyle({
      position: "fixed",
      left,
      top: rect.bottom + 10,
      width: tooltipWidth,
      ["--status-tooltip-arrow-left" as string]: `${arrowLeft}px`,
    });
  }, []);
  const hideTooltip = useCallback(() => {
    setActiveStatus(null);
    setTooltipStyle(undefined);
  }, []);

  let cumulativePercent = 0;
  return (
    <DashboardKpiCard
      title={t("salesProduct.shared.totalOrdersByStatus")}
      value={t("salesProduct.shared.totalOrdersValue", { value: resolvedTotalDisplay })}
      icon={<ShoppingCart size={22} />}
      tooltip={tooltip}
      formulaTitle={t("salesProduct.shared.formula")}
      hideNoTrendFallback
      footer={(
        <div className="sales-product-analytics-dashboard__status-kpi">
          <div className="sales-product-analytics-dashboard__status-kpi-bar">
            {rows.map(row => {
              const rawWidth = resolvedTotal > 0 ? (row.count / resolvedTotal) * 100 : 0;
              const width = Math.max(0, Math.min(rawWidth, 100 - cumulativePercent));
              cumulativePercent += width;
              const midpoint = Math.max(0, Math.min(100, cumulativePercent - width / 2));
              return (
                <span
                  key={row.status}
                  className="sales-product-analytics-dashboard__status-kpi-segment"
                  style={{
                    width: `${width}%`,
                    backgroundColor: SALES_STATUS_COLORS[row.status],
                    ["--status-segment-midpoint" as string]: `${midpoint}%`,
                  }}
                  onMouseEnter={event => showTooltip(row.status, event.currentTarget)}
                  onMouseLeave={hideTooltip}
                  onFocus={event => showTooltip(row.status, event.currentTarget)}
                  onBlur={hideTooltip}
                  tabIndex={0}
                  aria-label={`${row.label}: ${row.countFormattedValue ?? row.count.toLocaleString()} orders, ${row.percentFormattedValue ?? `${row.percent.toFixed(1)}%`}`}
                />
              );
            })}
            {activeRow ? (
              <div
                className="sales-product-analytics-dashboard__status-kpi-tooltip"
                style={tooltipStyle}
              >
                <span className="sales-product-analytics-dashboard__status-kpi-tooltip-label">
                  {t("salesProduct.shared.statusOrdersLabel", { status: activeRow.label })}
                </span>
                <span className="sales-product-analytics-dashboard__status-kpi-tooltip-description">
                  {t(`salesProduct.shared.statusDescription.${activeRow.status}`, {
                    defaultValue: SALES_STATUS_DESCRIPTIONS[activeRow.status],
                  })}
                </span>
                <strong>{activeRow.countFormattedValue ?? activeRow.count.toLocaleString()}</strong>
              </div>
            ) : null}
          </div>
        </div>
      )}
      className="sales-product-analytics-dashboard__status-kpi-card"
    />
  );
};

function exportOrderDetailPdf(order: SalesProductOrderDetailView) {
  const pdf = new jsPDF("p", "mm", "a4");
  const left = 16;
  let y = 18;
  const addLine = (label: string, value: string) => {
    pdf.setFont("helvetica", "bold");
    pdf.text(label, left, y);
    pdf.setFont("helvetica", "normal");
    pdf.text(value || "—", 70, y);
    y += 8;
  };

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.text(`Order ${order.orderName}`, left, y);
  y += 10;
  pdf.setFontSize(11);
  pdf.setFont("helvetica", "normal");
  pdf.text(`Generated from DataTram on ${dayjs().format("DD-MM-YYYY hh:mm A")}`, left, y);
  y += 12;

  pdf.setFontSize(12);
  addLine("Order Date/Time", formatOrderDateTime(order.orderDateTime));
  addLine("Payment status", order.financialStatus || "—");
  addLine("Fulfillment", order.fulfillmentStatus || "—");
  y += 4;
  addLine("Customer", order.customer.name);
  addLine("Phone", order.customer.phone);
  addLine("Email", order.customer.email);
  y += 4;
  addLine("Subtotal", order.subtotalFormattedValue ?? formatCurrency(order.subtotal));
  addLine("Shipping", order.shippingFormattedValue ?? formatCurrency(order.shipping));
  addLine("Total", order.totalFormattedValue ?? formatCurrency(order.total));
  y += 6;

  pdf.setFont("helvetica", "bold");
  pdf.text("Products", left, y);
  y += 8;
  order.lineItems.forEach((item, index) => {
    if (y > 270) {
      pdf.addPage();
      y = 18;
    }
    pdf.setFont("helvetica", "bold");
    pdf.text(`${index + 1}. ${item.productTitle}`, left, y);
    y += 7;
    pdf.setFont("helvetica", "normal");
    pdf.text(`Variant: ${item.variantTitle || "—"}`, left + 4, y);
    y += 6;
    pdf.text(`Price: ${item.priceFormattedValue ?? formatCurrency(item.price)}    Quantity: ${item.quantity}`, left + 4, y);
    y += 9;
  });

  pdf.save(`${order.orderName.replace(/[^a-z0-9-]+/gi, "_") || "order"}-detail.pdf`);
}

export const OrderDetailSidePanel: FC<{
  open: boolean;
  orderId: string | null;
  lang: ReturnType<typeof getApiLangFromI18n>;
  tenantId?: number;
  onClose: () => void;
}> = ({ open, orderId, lang, tenantId, onClose }) => {
  const { t } = useTranslation();
  const { order, loading, error } = useShopifySalesProductOrderDetail(orderId, lang, tenantId);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    globalThis.addEventListener("keydown", onKeyDown);
    return () => globalThis.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="sales-product-analytics-dashboard__order-panel-shell">
      <button
        type="button"
        className="sales-product-analytics-dashboard__order-panel-backdrop"
        onClick={onClose}
        aria-label={t("salesProduct.shared.orderPanel.closeAria")}
      />
      <aside
        className="sales-product-analytics-dashboard__order-panel"
        aria-label={t("salesProduct.shared.orderPanel.ariaLabel")}
        aria-modal="true"
        role="dialog"
      >
        <div className="sales-product-analytics-dashboard__order-panel-header">
          <div>
            <h3>{t("salesProduct.shared.orderPanel.title", { orderName: order?.orderName ?? t("salesProduct.shared.orderPanel.order") })}</h3>
            <span>
              {order
                ? t("salesProduct.shared.orderPanel.orderDateTime", { value: resolveOrderDateTimeDisplay(order, lang === "ar") })
                : t("salesProduct.shared.orderPanel.loadingInfo")}
            </span>
          </div>
          <button
            type="button"
            className="sales-product-analytics-dashboard__order-panel-close"
            onClick={onClose}
            aria-label={t("salesProduct.shared.orderPanel.closeAria")}
          >
            <X size={18} />
          </button>
        </div>

        {(() => {
          if (loading) {
            return (
              <div className="sales-product-analytics-dashboard__order-panel-state">
                <Spin />
                <span>{t("salesProduct.shared.orderPanel.loadingDetails")}</span>
              </div>
            );
          }
          if (error) {
            return (
              <div className="sales-product-analytics-dashboard__order-panel-state sales-product-analytics-dashboard__order-panel-state--error">
                {error}
              </div>
            );
          }
          if (order) {
            return (
              <>
                <section className="sales-product-analytics-dashboard__order-panel-section">
                  <div className="sales-product-analytics-dashboard__order-panel-section-head">
                    <h4>{t("salesProduct.shared.orderPanel.productDetails")}</h4>
                    <button
                      type="button"
                      className="sales-product-analytics-dashboard__order-panel-export"
                      onClick={() => exportOrderDetailPdf(order)}
                    >
                      <Download size={10} />
                      {t("salesProduct.shared.orderPanel.exportPdf")}
                    </button>
                  </div>
                  {order.lineItems.length > 0 ? (
                    <div className="sales-product-analytics-dashboard__order-products">
                      {order.lineItems.map(item => (
                        <div key={item.id} className="sales-product-analytics-dashboard__order-product">
                          <div className="sales-product-analytics-dashboard__order-product-image">
                            {item.imageUrl ? (
                              <img src={item.imageUrl} alt={item.productTitle} loading="lazy" />
                            ) : (
                              <Package size={22} aria-hidden />
                            )}
                          </div>
                          <div className="sales-product-analytics-dashboard__order-product-main">
                            <strong>{item.productTitle}</strong>
                            <span>{formatOrderPanelText(item.variantTitle)}</span>
                            <span className="sales-product-analytics-dashboard__order-product-price">
                              {item.priceFormattedValue ?? formatCurrency(item.price)}
                            </span>
                          </div>
                          <span className="sales-product-analytics-dashboard__order-product-qty">
                            {t("salesProduct.shared.orderPanel.qty", { count: item.quantity })}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="sales-product-analytics-dashboard__order-panel-empty">
                      {t("salesProduct.shared.orderPanel.noProductDetails")}
                    </p>
                  )}
                </section>

                <section className="sales-product-analytics-dashboard__order-panel-section">
                  <h4>{t("salesProduct.shared.orderPanel.financialSummary")}</h4>
                  <dl className="sales-product-analytics-dashboard__order-detail-list">
                    <div><dt>{t("salesProduct.shared.orderPanel.subtotal")}</dt><dd>{order.subtotalFormattedValue ?? formatCurrency(order.subtotal)}</dd></div>
                    <div><dt>{t("salesProduct.shared.orderPanel.shipping")}</dt><dd>{order.shippingFormattedValue ?? formatCurrency(order.shipping)}</dd></div>
                    <div className="sales-product-analytics-dashboard__order-detail-list-total"><dt>{t("salesProduct.shared.orderPanel.total")}</dt><dd>{order.totalFormattedValue ?? formatCurrency(order.total)}</dd></div>
                    <div className="sales-product-analytics-dashboard__order-detail-list-status"><dt>{t("salesProduct.shared.orderPanel.paymentStatus")}:</dt><dd><span className={`sales-product-analytics-dashboard__order-status-badge sales-product-analytics-dashboard__order-status-badge--${getOrderStatusBadgeModifier(order.financialStatus)}`}>{formatOrderStatusLabelDisplay(order.financialStatus)}</span></dd></div>
                    <div><dt>{t("salesProduct.shared.orderPanel.status")}</dt><dd>{order.financialStatus || "—"}</dd></div>
                  </dl>
                </section>

                <section className="sales-product-analytics-dashboard__order-panel-section">
                  <h4>{t("salesProduct.shared.orderPanel.customerInformation")}</h4>
                  <dl className="sales-product-analytics-dashboard__order-detail-list sales-product-analytics-dashboard__order-detail-list--customer">
                    <div><dt>{t("salesProduct.shared.orderPanel.name")}</dt><dd>{formatOrderPanelText(order.customer.name)}</dd></div>
                    <div><dt>{t("salesProduct.shared.orderPanel.phone")}</dt><dd>{formatOrderPanelText(order.customer.phone)}</dd></div>
                    <div><dt>{t("salesProduct.shared.orderPanel.email")}</dt><dd>{formatOrderPanelText(order.customer.email)}</dd></div>
                    <div><dt>{t("salesProduct.shared.orderPanel.orderDateTimeLabel")}</dt><dd>{resolveOrderDateTimeDisplay(order, lang === "ar")}</dd></div>
                  </dl>
                </section>
              </>
            );
          }
          return (
            <div className="sales-product-analytics-dashboard__order-panel-state">
              {t("salesProduct.shared.orderPanel.noOrderDetails")}
            </div>
          );
        })()}
      </aside>
    </div>,
    document.body,
  );
};
